'use client';

import { useState } from 'react';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Progress } from 'components/ui/progress';
import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  CircleCheckBig,
  Download,
  FileText,
  MessageCircleWarning,
  MessageSquare,
  MoveLeft,
  Paperclip,
  TriangleAlert,
  XCircle,
} from 'lucide-react';
import ResponsiblePicker from './responsible-picker';
import AuditDeadlinePicker from './audit-deadline-picker';
import { useHistory, useParams } from 'react-router';
import { SubTasksMap, TasksMap, useAudit } from './audit-context';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tooltip, TooltipTrigger, TooltipContent } from 'components/ui/tooltip';
import firebase from 'firebase/compat/app';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '../InternalAudit/api';
import PageLoader from 'components/Loader/PageLoader';
import {
  AuditContent,
  getStatusColor,
  getStatusIcon,
} from '../InternalAuditOverview/components/AuditMatrix';
import { auditTypes } from '../InternalAudit/audit-type-selection';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import { format } from 'date-fns';

export function getDisplayStatus(status: string) {
  switch (status?.toLowerCase()) {
    case 'deviation':
    case 'deviation-rejected':
      return 'Afvigelse';
    case 'deviation-onhold':
      return 'Håndteret afventer handling';
    case 'deviation-pending':
      return 'Afvigelse afventer godkendelse';
    case 'observation':
      return 'Observation';
    case 'observation-completed':
      return 'Observation afsluttet';
    case 'passed':
      return 'Godkendt';
    case 'not-audited':
      return 'Ikke auditeret';

    case 'deviation-resolved':
      return 'Afvigelse løst';
    default:
      return 'Ikke auditeret';
  }
}

interface DeviationStateMap {
  [key: string]: {
    status: string;
    responsible: string;
    dueDate: Date;
  };
}

export function getCounts(
  subTasksMap: SubTasksMap | null,
  deviationState?: DeviationStateMap | null
) {
  const initCounts = {
    deviations: 0,
    observations: 0,
    observationsResolved: 0,
    passed: 0,
    total: 0,
    resolved: 0,
    onHold: 0,
    pending: 0,
  };
  if (!subTasksMap) {
    return initCounts;
  }
  const counts = Object.entries(subTasksMap).reduce((acc, [_, subTask]) => {
    if (deviationState?.[subTask.id]?.status === 'deviation-pending') {
      return {
        ...acc,
        pending: acc.pending + 1,
      };
    }
    if (deviationState?.[subTask.id]?.status === 'observation-completed') {
      return {
        ...acc,
        observationsResolved: acc.observationsResolved + 1,
      };
    }
    if (deviationState?.[subTask.id]?.status === 'deviation-resolved') {
      return {
        ...acc,
        resolved: acc.resolved + 1,
      };
    }
    if (deviationState?.[subTask.id]?.status === 'deviation-onhold') {
      return {
        ...acc,
        onHold: acc.onHold + 1,
      };
    }
    return {
      ...acc,
      deviations: subTask.status === 'deviation' ? acc.deviations + 1 : acc.deviations,
      observations: subTask.status === 'observation' ? acc.observations + 1 : acc.observations,
      passed: subTask.status === 'passed' ? acc.passed + 1 : acc.passed,
      observationsResolved:
        subTask.status === 'observation-completed'
          ? acc.observationsResolved + 1
          : acc.observationsResolved,
      // we don't count not-audited tasks
      total: !subTask.status || subTask.status !== 'not-audited' ? acc.total + 1 : acc.total,
    };
  }, initCounts);
  return counts;
}
function getCountsByTask(subTasksMap: SubTasksMap | null, taskId: string) {
  const initCounts = {
    deviations: 0,
    observations: 0,
    passed: 0,
    total: 0,
  };
  if (!subTasksMap) {
    return initCounts;
  }
  const counts = Object.entries(subTasksMap)
    .filter(([_, task]) => task.taskId === taskId)
    .reduce((acc, [_, subTask]) => {
      return {
        ...acc,
        deviations: subTask.status === 'deviation' ? acc.deviations + 1 : acc.deviations,
        observations: subTask.status === 'observation' ? acc.observations + 1 : acc.observations,
        passed: subTask.status === 'passed' ? acc.passed + 1 : acc.passed,

        // we don't count not-audited tasks
        total: !subTask.status || subTask.status !== 'not-audited' ? acc.total + 1 : acc.total,
      };
    }, initCounts);
  return counts;
}

function validateAllSubTasksDeviation(subTasksMap: SubTasksMap | null) {
  if (!subTasksMap) {
    return false;
  }
  // check if all subtasks with state deviation has a responsible and due date
  const allDeviationSubTasks = Object.values(subTasksMap).filter(
    (subTask) => subTask.status === 'deviation' || subTask.status === 'observation'
  );
  return allDeviationSubTasks.every((subTask) => subTask.responsible && subTask.dueDate);
}

function getTasksList(tasksMap: TasksMap | null) {
  if (!tasksMap) {
    return [];
  }
  return Object.entries(tasksMap)
    .map(([_, task]) => ({
      ...task,
    }))
    .sort((a, b) => a.order - b.order);
}

export default function AuditSummary() {
  const { t } = useTranslation();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { audit, tasksMap, subTasksMap, currentTaskIndex, setCurrentTaskIndex, setStep } =
    useAudit();

  if (!audit || !tasksMap) return <PageLoader />;

  const counts = getCounts(subTasksMap);

  const isCompleted = audit.state === 'COMPLETED';

  const submitAuditComplete = async () => {
    const isValid = validateAllSubTasksDeviation(subTasksMap);
    if (!isValid) {
      toast.error('Alle afvigelser og observationer skal have en ansvarlig og en deadline');
      return;
    }
    setIsSubmitting(true);
    if (!isCompleted) {
      await api.completeInternalAudit({ auditId: audit.id });
      toast.success(
        `${counts.deviations} afvigelse(r) og ${counts.observations} observation(er) oprettet`
      );
    }
    setIsSubmitting(false);
    history.push(`/internal-audit`);
  };

  const passRate = Math.round((counts.passed / counts.total) * 100);

  const taskList = getTasksList(tasksMap);
  const auditType = auditTypes?.find((type) => type.id === audit.type);
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl full-width-print no-padding-print">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <Button size="sm" variant="secondary" onClick={() => setStep('execution')}>
            <MoveLeft size={14} strokeWidth={1} /> Tilbage
          </Button>
          <div className="mt-2" />
          <h1 className="flex gap-2 text-2xl font-bold tracking-tight items-center">
            {audit.location.title} {!isCompleted && <Badge variant="outline">Kladde</Badge>}
          </h1>
          <p className="text-muted-foreground">
            {isCompleted
              ? t('general.dateTimeYYYY', { date: audit.completeTime?.toDate() })
              : 'Igangværende audit'}
            • {auditType?.title || 'intern audit'} • Auditør: {audit.auditor.displayName}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const originalTitle = document.title;
              document.title = `${audit.location.title}${
                isCompleted ? ` - ${format(audit.completeTime?.toDate(), 'yyyy-MM-dd')}` : ''
              }`;
              window.print();
              document.title = originalTitle;
            }}
          >
            <Download className="mr-2 h-4 w-4" />
            Gem PDF
          </Button>
          <Button size="sm" onClick={submitAuditComplete} loading={isSubmitting}>
            <FileText className="mr-2 h-4 w-4" />
            {isCompleted ? 'Til oversigten' : 'Afslut audit & opret opgaver'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-6 no-print">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Afvigelser</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-500">{counts.deviations}</div>
            <Progress
              value={(counts.deviations / counts.total) * 100}
              className="h-2 mt-2 bg-slate-200"
              //   indicatorClassName="bg-red-500"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Observationer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-500">{counts.observations}</div>
            <Progress
              value={(counts.observations / counts.total) * 100}
              className="h-2 mt-2 bg-slate-200"
              //   indicatorClassName="bg-amber-500"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Godkendt</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-500">{counts.passed}</div>
            <Progress
              value={(counts.passed / counts.total) * 100}
              className="h-2 mt-2 bg-slate-200"
              //   indicatorClassName="bg-amber-500"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Godkendelsesprocent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-500">{passRate}%</div>
            <Progress
              value={passRate}
              className="h-2 mt-2 bg-slate-200"
              //   indicatorClassName="bg-green-500"
            />
          </CardContent>
        </Card>
      </div>

      <Card className="no-shadow-print no-border-print no-padding-print">
        <CardHeader className="no-padding-print no-print">
          <CardTitle className="text-xl font-bold ">Opgaver fra auditering</CardTitle>
        </CardHeader>
        <CardContent className="no-padding-print">
          {/* <Tabs defaultValue="all">
              <TabsList className="mb-4">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="critical" className="text-red-500">
                  critical
                </TabsTrigger>
                <TabsTrigger value="major" className="text-amber-500">
                  major
                </TabsTrigger>
              </TabsList> */}
          {/* <TabsContent value="all"> */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[500px]">Opgave</TableHead>
                  <TableHead>Status</TableHead>
                  {/* <TableHead>Sales</TableHead> */}
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taskList.map((item) => (
                  <NestedTableRow key={item.id} item={item} isCompleted={isCompleted} />
                ))}
              </TableBody>
            </Table>
          </div>
          {/* <div className="rounded border">
            <div className="grid grid-cols-12 bg-muted p-3 font-medium rounded">
              <div className="col-span-5">Finding</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-5 flex justify-end">Action</div>
            </div>
            {taskList.map((finding) => (
              <div key={finding.id} className="grid grid-cols-12 p-3 border-t">
                <div className="col-span-5 flex items-center gap-2">
                  {getStatusIcon(finding.status)}
                  <span>{finding.label}</span>
                </div>
                <div className="col-span-2 flex items-center">
                  <Badge className={getStatusColor(finding.status)}>
                    {getDisplayStatus(finding.status)}
                  </Badge>
                </div>
                <div className="flex flex-col items-end justify-end col-span-5 gap-1">
                  {finding.status === 'critical' && (
                    <>
                      <div className="flex items-center gap-2">
                        <TriangleAlert color="red" size={16} />
                        <div className="text-xs">Opretter opgave til </div>
                        <div className="">
                          <ResponsiblePicker />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs">deadline</div>
                        <div>
                          <AuditDeadlinePicker />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div> */}
          {/* </TabsContent> */}
          {/* </Tabs> */}
        </CardContent>
      </Card>

      {/* Attachments Appendix */}
      <AttachmentsAppendix tasksMap={tasksMap} subTasksMap={subTasksMap} />
    </div>
  );
}

// Helper function to collect all attachments with their context and appendix numbers
function collectAllAttachments(tasksMap: TasksMap | null, subTasksMap: SubTasksMap | null) {
  if (!tasksMap || !subTasksMap) return [];

  const attachments: Array<{
    attachment: any;
    taskLabel: string;
    subTaskLabel: string;
    taskId: string;
    subTaskId: string;
    appendixNumber: number;
  }> = [];

  let appendixCounter = 1;

  // Get tasks sorted by order to maintain chronological sequence
  const sortedTasks = Object.values(tasksMap).sort((a, b) => a.order - b.order);

  // Process attachments in task order
  sortedTasks.forEach((task) => {
    if (task.subTaskIds?.length > 0) {
      task.subTaskIds.forEach((subTaskId: string) => {
        const subTask = subTasksMap[subTaskId];
        if (subTask?.attachments?.length > 0) {
          subTask.attachments.forEach((attachment: any) => {
            attachments.push({
              attachment,
              taskLabel: task.label || 'Unknown Task',
              subTaskLabel: subTask.label,
              taskId: task.id,
              subTaskId: subTask.id,
              appendixNumber: appendixCounter++,
            });
          });
        }
      });
    }
  });

  return attachments;
}

// Helper function to get appendix number for a specific attachment
function getAppendixNumber(
  subTaskId: string,
  attachmentIndex: number,
  tasksMap: TasksMap | null,
  subTasksMap: SubTasksMap | null
): number | null {
  if (!tasksMap || !subTasksMap) return null;

  let currentIndex = 0;

  // Get tasks sorted by order to maintain chronological sequence (same as collectAllAttachments)
  const sortedTasks = Object.values(tasksMap).sort((a, b) => a.order - b.order);

  // Process attachments in task order
  for (const task of sortedTasks) {
    if (task.subTaskIds?.length > 0) {
      for (const subTaskIdInTask of task.subTaskIds) {
        const subTask = subTasksMap[subTaskIdInTask];
        if (subTask?.attachments?.length > 0) {
          for (let i = 0; i < subTask.attachments.length; i++) {
            if (subTask.id === subTaskId && i === attachmentIndex) {
              return currentIndex + 1;
            }
            currentIndex++;
          }
        }
      }
    }
  }
  return null;
}

type AttachmentsAppendixProps = {
  tasksMap: TasksMap | null;
  subTasksMap: SubTasksMap | null;
};

function AttachmentsAppendix({ tasksMap, subTasksMap }: AttachmentsAppendixProps) {
  const allAttachments = collectAllAttachments(tasksMap, subTasksMap);

  if (allAttachments.length === 0) {
    return null;
  }

  return (
    <div className="hidden print-display mt-6 no-shadow-print no-border-print no-padding-print">
      {/* <div className="no-padding-print">
        <h3 className="text-xl font-bold">Bilag</h3>
        <p className="text-sm text-muted-foreground">
          Alle vedhæftede filer og billeder fra auditering
        </p>
      </div> */}
      {/* <CardContent className="no-padding-print"> */}
      <div className="w-full space-y-4">
        <div className="mb-4">
          <h3 className="text-xl font-bold">Bilag</h3>
          <p className="text-sm text-muted-foreground">Alle vedhæftede billeder fra auditering</p>
        </div>
        {allAttachments.map((item, index) => (
          <div key={`${item.subTaskId}-${index}`} className="avoid-break">
            <div className="mb-3">
              <h4 className="font-semibold text-lg text-gray-900">Bilag {item.appendixNumber}</h4>
              <p className="text-sm text-gray-700 mt-1">
                {item.taskLabel} → {item.subTaskLabel}
              </p>
              {/* <p className="text-xs text-gray-500 mt-1">
                  Opgave ID: {item.taskId} | Delopgave ID: {item.subTaskId}
                </p> */}
            </div>

            <div className="space-y-3">
              {item.attachment.type?.includes('image') ? (
                <div className="flex flex-col gap-2">
                  <img
                    src={item.attachment.download}
                    alt={item.attachment.name || 'Attachment'}
                    className="max-w-full h-auto rounded-lg border shadow-sm"
                  />
                  {item.attachment.name && (
                    <p className="text-sm text-gray-600 font-medium">{item.attachment.name}</p>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
                  <Paperclip className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-sm">{item.attachment.name || 'Unnamed file'}</p>
                    <p className="text-xs text-gray-500">
                      {item.attachment.type || 'Unknown type'}
                    </p>
                  </div>
                  {item.attachment.download && (
                    <Button variant="outline" size="sm" asChild className="ml-auto">
                      <a href={item.attachment.download} target="_blank" rel="noopener noreferrer">
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </a>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* </CardContent> */}
    </div>
  );
}

type NestedTableRowProps = {
  item: any;
  isCompleted: boolean;
};

function NestedTableRow({ item, isCompleted }: NestedTableRowProps) {
  const [expanded, setExpanded] = useState(true);
  const { subTasksMap } = useAudit();
  const hasChildren = item.subTaskIds?.length > 0;
  const { deviations, observations, passed, total } = getCountsByTask(subTasksMap, item.id);
  return (
    <>
      <TableRow className="group bg-slate-50">
        <TableCell>
          <div className="flex items-center">
            {/* {hasChildren ? (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mr-2 flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="mr-2 w-5" />
            )} */}
            <div className="flex gap-2">
              {/* <div className="flex items-center gap-[-10px]">
                {deviations > 0 && <XCircle className="h-5 w-5 text-red-500" />}
                {observations > 0 && <AlertTriangle className="h-5 w-5 text-amber-500" />}
                {passed > 0 && <CheckCircle className="h-5 w-5 text-green-500" />}
              </div> */}
              <span className="font-bold">{item.label}</span>
            </div>
          </div>
        </TableCell>
        <TableCell className="no-print"></TableCell>
        <TableCell className="flex gap-2 flex-col items-end">
          <Badge variant="outline" className="print-audit-badge-w print-audit-badge-text">
            {item.category}
          </Badge>
        </TableCell>
      </TableRow>
      {hasChildren && expanded && (
        <>
          {item.subTaskIds!.map((child: any) => (
            <NestedTableRowSubTask
              key={child.id}
              taskId={item.id}
              id={child}
              isCompleted={isCompleted}
            />
          ))}
        </>
      )}
    </>
  );
}
function NestedTableRowSubTask({
  id,
  taskId,
  isCompleted,
}: {
  id: string;
  taskId: string;
  isCompleted: boolean;
}) {
  const { audit, tasksMap, subTasksMap } = useAudit();
  const { auditId } = useParams<{ auditId: string }>();
  const subTask = subTasksMap![id];
  const deviationStateMap = audit.deviationState;

  const handleUpdateSubTaskResonsible = async (userId: string) => {
    try {
      if (!auditId || !id) {
        throw new Error(`Missing auditId=${auditId} or taskId=${id}`);
      }
      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`subTasksMap.${id}.responsible`]: { id: userId, displayName: 'Test' },
        });
      toast.success('Ansvarlig opdateret');
    } catch (error) {
      toast.error('Fejl ved opdatering af ansvarlig');
      console.error(error);
    }
  };
  const handleUpdateSubTaskDueDate = async (date: Date | null) => {
    try {
      if (!auditId || !id) {
        throw new Error(`Missing auditId=${auditId} or taskId=${id}`);
      }

      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`subTasksMap.${id}.dueDate`]: date,
        });
      toast.success('Deadline opdateret');
    } catch (error) {
      toast.error('Fejl ved opdatering af deadline');
      console.error(error);
    }
  };

  const hasResolvedDeviation = deviationStateMap?.[subTask.id]?.status || subTask.status;
  console.log({
    hasResolvedDeviation,
  });

  return (
    <>
      <TableRow className="group-">
        <TableCell>
          <div className="flex">
            <div className="mr-4" />
            <div className="flex gap-3">
              <div>{subTask.label}</div>
              <div className="flex items-center gap-3 no-print">
                {subTask.description && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1 text-xs">
                        <MessageSquare className="h-4 w-4 text-slate-500" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="start" className="max-w-sm p-3">
                      <div className="text-sm font-medium whitespace-pre-wrap">
                        {subTask.description}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}
                {subTask.attachments?.length > 0 && (
                  <div className="flex items-center gap-1 text-xs">
                    <Paperclip className="h-4 w-4 text-slate-500" />
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700">
                      {subTask.attachments
                        ?.map((attachment, attachmentIndex) => {
                          const appendixNum = getAppendixNumber(
                            subTask.id,
                            attachmentIndex,
                            tasksMap,
                            subTasksMap
                          );
                          if (!attachment.type?.includes('image')) return;
                          return `Bilag ${appendixNum}`;
                        })
                        .join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="hidden print-display ml-4 text-muted-foreground text-xs whitespace-break-spaces">
            {subTask.description}
          </div>
          {subTask.attachments?.length > 0 && (
            <div className="no-print ml-4 text-muted-foreground text-xs flex-wrap gap-1 mt-2">
              {subTask.attachments?.map((attachment, attachmentIndex) => {
                console.log({ attachment });
                if (!attachment.type?.includes('image')) return;
                const appendixNum = getAppendixNumber(
                  subTask.id,
                  attachmentIndex,
                  tasksMap,
                  subTasksMap
                );
                return (
                  <Dialog key={attachmentIndex}>
                    <DialogTrigger asChild>
                      <div className="flex items-center gap-1 text-xs cursor-pointer hover:opacity-80 transition-opacity">
                        <img
                          src={attachment.download}
                          className="h-8 w-8 rounded-md object-cover"
                        />
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                      <DialogHeader>
                        <DialogTitle>Bilag {appendixNum}</DialogTitle>
                        <p className="text-sm text-muted-foreground">
                          {tasksMap![subTask.taskId]?.label} → {subTask.label}
                        </p>
                      </DialogHeader>
                      <div className="flex justify-center">
                        <img
                          src={attachment.download}
                          alt={`Bilag ${appendixNum}`}
                          className="max-w-full max-h-[70vh] object-contain rounded-lg"
                        />
                      </div>
                      <p className="text-center text-sm text-gray-600 mt-2">Bilag {appendixNum}</p>
                    </DialogContent>
                  </Dialog>
                );
              })}
            </div>
          )}
          {subTask.attachments?.length > 0 && (
            <div className="hidden print-display ml-4 text-muted-foreground text-xs flex-wrap gap-1 mt-2">
              {subTask.attachments?.map((attachment, attachmentIndex) => {
                console.log({ attachment });
                const appendixNum = getAppendixNumber(
                  subTask.id,
                  attachmentIndex,
                  tasksMap,
                  subTasksMap
                );
                if (!attachment.type?.includes('image')) return;
                return (
                  <div key={attachmentIndex} className="flex items-center gap-1 text-xs">
                    <img src={attachment.download} className="max-h-16 w-16 rounded-lg" />
                    <span className="text-xs font-medium text-gray-600">Bilag {appendixNum}</span>
                  </div>
                );
              })}
            </div>
          )}
        </TableCell>
        <TableCell>
          <Dialog key={audit.location.id}>
            <DialogTrigger asChild disabled={false}>
              <div className="flex">
                <Badge
                  className={
                    getStatusColor(hasResolvedDeviation) +
                    ' flex gap-1 cursor-pointer no-shadow-print print-audit-badge-text'
                  }
                >
                  {getStatusIcon(hasResolvedDeviation)}
                  {getDisplayStatus(hasResolvedDeviation)}
                </Badge>
              </div>
            </DialogTrigger>
            <DialogContent className="overflow-y-auto max-h-[calc(100vh-100px)]">
              <DialogHeader>
                <DialogTitle>{audit.location.title}</DialogTitle>
              </DialogHeader>
              <AuditContent
                audit={{ ...audit, tasksMap, subTasksMap }}
                taskId={taskId}
                subTaskId={id}
              />
            </DialogContent>
          </Dialog>
        </TableCell>
        {/* <TableCell>{subTask.status}</TableCell> */}
        <TableCell className="flex gap-2 flex-col items-end no-print">
          {/* 
            TODO: should we add observations to this? and create a task?
          || subTask.status === 'observation' */}
          {(subTask.status === 'deviation' || subTask.status === 'observation') && (
            <>
              <div className="flex items-center gap-1">
                {subTask.responsible ? (
                  <Check className="text-green-500" size={16} />
                ) : (
                  <TriangleAlert color="red" size={16} />
                )}
                <div className="text-xs">Opret opgave til </div>
                <div className="">
                  <ResponsiblePicker
                    disabled={isCompleted}
                    value={subTask.responsible?.id}
                    onChange={handleUpdateSubTaskResonsible}
                  />
                </div>
              </div>
              <div className="flex items-center gap-1">
                {subTask.dueDate ? (
                  <Check className="text-green-500" size={16} />
                ) : (
                  <TriangleAlert color="red" size={16} />
                )}
                <div className="text-xs">Deadline</div>
                <div>
                  <AuditDeadlinePicker
                    // @ts-ignore
                    deadline={subTask.dueDate?.toDate()}
                    setDeadline={handleUpdateSubTaskDueDate}
                    disabled={isCompleted}
                  />
                </div>
              </div>
            </>
          )}
        </TableCell>
      </TableRow>
    </>
  );
}
